import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, RefreshCw, Edit2, X, Check, Database } from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const SOURCE_TYPES = [
  { value: 'interne', label: 'Interne' },
  { value: 'formulaire', label: 'Formulaire' },
  { value: 'zapier', label: 'Zapier' },
  { value: 'call', label: 'Call Center' },
  { value: 'ringover', label: 'Ringover' },
  { value: 'autre', label: 'Autre' },
];

export default function LeadSourcesConfigPanel() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSource, setNewSource] = useState({ id: '', nom: '', type: 'interne', webhook_url: '', actif: true });
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  });

  const loadTenants = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/billing/admin/overview`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        const list = (data.tenants || []).filter(t => t.tenant_id);
        setTenants(list);
        if (list.length > 0 && !selectedTenant) {
          setSelectedTenant(list[0].tenant_id);
        }
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadSources = useCallback(async () => {
    if (!selectedTenant) return;
    setLoading(true);
    try {
      const r = await authFetch(`${API}/config/pipeline/sources?tenant_id=${selectedTenant}`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setSources(data.sources || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedTenant]);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { if (selectedTenant) loadSources(); }, [selectedTenant, loadSources]);

  const handleAdd = async () => {
    if (!newSource.id.trim() || !newSource.nom.trim()) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/config/pipeline/sources/add?tenant_id=${selectedTenant}`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ ...newSource, webhook_url: newSource.webhook_url || null }),
      });
      if (r.ok) {
        setShowAddForm(false);
        setNewSource({ id: '', nom: '', type: 'interne', webhook_url: '', actif: true });
        await loadSources();
      } else {
        const err = await r.json();
        alert(err.detail || 'Erreur');
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (sourceId) => {
    if (!window.confirm(`Supprimer la source "${sourceId}" ?`)) return;
    try {
      const r = await authFetch(`${API}/config/pipeline/sources/${encodeURIComponent(sourceId)}?tenant_id=${selectedTenant}`, {
        method: 'DELETE', headers: getHeaders(),
      });
      if (r.ok) await loadSources();
    } catch (e) { console.error(e); }
  };

  const handleEdit = (source) => {
    setEditingId(source.id);
    setEditForm({ ...source });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/config/pipeline/sources/${encodeURIComponent(editingId)}?tenant_id=${selectedTenant}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ ...editForm, webhook_url: editForm.webhook_url || null }),
      });
      if (r.ok) {
        setEditingId(null);
        await loadSources();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const toggleActif = async (source) => {
    const updated = sources.map(s => s.id === source.id ? { ...s, actif: !s.actif } : s);
    setSources(updated);
    try {
      await authFetch(`${API}/config/pipeline/sources?tenant_id=${selectedTenant}`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify(updated),
      });
    } catch (e) { console.error(e); }
  };

  const tenantName = tenants.find(t => t.tenant_id === selectedTenant)?.name || selectedTenant;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/30 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
            <Database className="w-5 h-5 text-teal-400" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-white" data-testid="lead-sources-title">Sources de Lead</h2>
            <p className="text-sm text-slate-400">Gerez les sources de lead par societe</p>
          </div>
          <button onClick={loadSources} className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors" data-testid="refresh-sources-btn">
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tenant selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-400 font-medium whitespace-nowrap">Societe :</label>
        <select
          value={selectedTenant}
          onChange={e => setSelectedTenant(e.target.value)}
          data-testid="tenant-selector"
          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-teal-500 focus:outline-none"
        >
          {tenants.map(t => (
            <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Sources list */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-slate-700">
          <span className="text-sm font-medium text-slate-300">{sources.length} source(s) pour {tenantName}</span>
          <button
            onClick={() => setShowAddForm(true)}
            data-testid="add-source-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-500/20 text-teal-400 rounded-lg text-xs font-medium hover:bg-teal-500/30 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className="p-3 border-b border-slate-700 bg-slate-900/50 space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <input
                placeholder="ID (valeur en base)"
                value={newSource.id}
                onChange={e => setNewSource({ ...newSource, id: e.target.value })}
                data-testid="new-source-id"
                className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:border-teal-500 focus:outline-none"
              />
              <input
                placeholder="Nom affiche"
                value={newSource.nom}
                onChange={e => setNewSource({ ...newSource, nom: e.target.value })}
                data-testid="new-source-nom"
                className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:border-teal-500 focus:outline-none"
              />
              <select
                value={newSource.type}
                onChange={e => setNewSource({ ...newSource, type: e.target.value })}
                data-testid="new-source-type"
                className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:border-teal-500 focus:outline-none"
              >
                {SOURCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <div className="flex gap-1">
                <button onClick={handleAdd} disabled={saving || !newSource.id.trim() || !newSource.nom.trim()}
                  data-testid="confirm-add-source-btn"
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-teal-500/20 text-teal-400 rounded text-xs font-medium hover:bg-teal-500/30 disabled:opacity-50 transition-colors">
                  <Check className="w-3.5 h-3.5" /> Ajouter
                </button>
                <button onClick={() => setShowAddForm(false)}
                  className="px-2 py-2 bg-slate-700 text-slate-400 rounded hover:bg-slate-600 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="p-6 text-center text-slate-500">Chargement...</div>
        ) : sources.length === 0 ? (
          <div className="p-6 text-center text-slate-500">Aucune source configuree pour ce tenant</div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {sources.map((source) => (
              <div key={source.id} className={`flex items-center gap-3 p-3 hover:bg-slate-700/20 transition-colors ${!source.actif ? 'opacity-50' : ''}`}>
                {editingId === source.id ? (
                  <>
                    <input value={editForm.id} onChange={e => setEditForm({ ...editForm, id: e.target.value })}
                      className="flex-1 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm" />
                    <input value={editForm.nom} onChange={e => setEditForm({ ...editForm, nom: e.target.value })}
                      className="flex-1 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm" />
                    <select value={editForm.type} onChange={e => setEditForm({ ...editForm, type: e.target.value })}
                      className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm">
                      {SOURCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <button onClick={handleSaveEdit} disabled={saving}
                      className="p-1.5 rounded bg-teal-500/20 text-teal-400 hover:bg-teal-500/30 transition-colors">
                      <Save className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditingId(null)}
                      className="p-1.5 rounded bg-slate-700 text-slate-400 hover:bg-slate-600 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">{source.nom}</div>
                      <div className="text-xs text-slate-500 truncate">ID: {source.id}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      source.type === 'interne' ? 'bg-blue-500/20 text-blue-400' :
                      source.type === 'formulaire' ? 'bg-green-500/20 text-green-400' :
                      source.type === 'zapier' ? 'bg-orange-500/20 text-orange-400' :
                      source.type === 'call' ? 'bg-purple-500/20 text-purple-400' :
                      source.type === 'ringover' ? 'bg-cyan-500/20 text-cyan-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {SOURCE_TYPES.find(t => t.value === source.type)?.label || source.type}
                    </span>
                    <button
                      onClick={() => toggleActif(source)}
                      data-testid={`toggle-source-${source.id}`}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                        source.actif ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      }`}
                    >
                      {source.actif ? 'Actif' : 'Inactif'}
                    </button>
                    <button onClick={() => handleEdit(source)}
                      className="p-1.5 rounded bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(source.id)}
                      data-testid={`delete-source-${source.id}`}
                      className="p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
